import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { api } from '../lib/api.js';
import { useApiData } from '../lib/hooks.js';
import { useToast } from '../lib/store.js';
import { mondayOf, isoToday, addDays, fmtDate, num, signed } from '../lib/format.js';
import { Card, Button, Spinner, ErrorNote, Pill, SectionTitle, PageHeader, TextArea, Sparkline } from '../components/ui.jsx';

// The weekly review. It opens with what went well, reports trends rather than
// verdicts, and puts any proposed change at the bottom with a decline button
// next to it.

export default function Review() {
  const [weekStart, setWeekStart] = useState(mondayOf(isoToday()));
  const { data, loading, error, reload } = useApiData(`/review?week_start=${weekStart}`, [weekStart]);
  const { show } = useToast();
  const [notes, setNotes] = useState(null);
  const [busy, setBusy] = useState(false);

  if (loading && !data) return <Spinner label="Looking back over the week" />;
  if (error) return <div className="p-gutter"><ErrorNote error={error} onRetry={reload} /></div>;
  if (!data) return null;

  const m = data.metrics;
  const isThisWeek = weekStart === mondayOf(isoToday());

  async function respond(id, decision) {
    setBusy(true);
    try {
      await api.post('/suggestions', { id, decision });
      show(decision === 'accept' ? 'Updated.' : 'Left as it is.');
      reload({ quiet: true });
    } catch (err) {
      show(err.message, 'crit');
    } finally {
      setBusy(false);
    }
  }

  const weightSeries = data.history
    .filter((h) => h.metrics?.weightAvgKg != null)
    .map((h) => ({ date: h.week_start, value: h.metrics.weightAvgKg }));

  return (
    <div className="max-w-lg mx-auto">
      <PageHeader title="Weekly review" subtitle={`${fmtDate(data.weekStart, 'short')} – ${fmtDate(data.weekEnd, 'short')}${isThisWeek ? ' · in progress' : ''}`} />

      <div className="px-gutter">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => setWeekStart(addDays(weekStart, -7))} className="p-2 -ml-2 text-textSoft" aria-label="Previous week">
            <ChevronLeft size={20} />
          </button>
          <span className="text-meta text-textMute">Programme week {data.planWeek}</span>
          <button onClick={() => setWeekStart(addDays(weekStart, 7))} disabled={isThisWeek} className="p-2 -mr-2 text-textSoft disabled:opacity-30" aria-label="Next week">
            <ChevronRight size={20} />
          </button>
        </div>

        <Card className="mb-4">
          <h2 className="text-card-title font-display mb-3">{data.headline}</h2>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-label uppercase text-textMute mb-1">Sessions</p>
              <p className="text-lg font-semibold">{m.sessionsDone}<span className="text-textMute text-body-sm">/{m.sessionsPlanned}</span></p>
            </div>
            <div>
              <p className="text-label uppercase text-textMute mb-1">Avg steps</p>
              <p className="text-lg font-semibold">{m.avgSteps != null ? num(m.avgSteps) : '—'}</p>
            </div>
            <div>
              <p className="text-label uppercase text-textMute mb-1">Avg protein</p>
              <p className="text-lg font-semibold">{m.avgProtein != null ? `${m.avgProtein}g` : '—'}</p>
            </div>
          </div>
          {m.weightChangeKg != null && (
            <p className="text-body-sm text-textSoft mt-4 pt-3 border-t border-edge">
              Weekly average weight {signed(m.weightChangeKg, 'kg', 2)} on the week before.
            </p>
          )}
        </Card>

        {data.observations?.length > 0 && (
          <Card className="mb-4">
            <SectionTitle>What the week looked like</SectionTitle>
            <ul className="space-y-2">
              {data.observations.map((n, i) => (
                <li key={i} className="text-body-sm text-textSoft flex gap-2">
                  <span className="text-accent shrink-0">·</span>
                  {n}
                </li>
              ))}
            </ul>
          </Card>
        )}

        {weightSeries.length >= 3 && (
          <Card className="mb-4">
            <SectionTitle>Weekly averages</SectionTitle>
            <Sparkline points={weightSeries} tone="s1" showDots label="Weekly average weight" />
            <p className="text-meta text-textMute mt-2">One point per week. This is the line worth watching, not any single morning.</p>
          </Card>
        )}

        {data.trendNote && <p className="text-meta text-textMute mb-4">{data.trendNote}</p>}

        {data.suggestions.length > 0 && (
          <>
            <SectionTitle>Worth considering</SectionTitle>
            <div className="space-y-3 mb-4">
              {data.suggestions.map((s) => (
                <Card key={s.id} className="border-accent/40">
                  <Pill tone="accent">{s.kind}</Pill>
                  <h3 className="text-card-title font-display mt-2.5 mb-1.5">{s.title}</h3>
                  <p className="text-body-sm text-textSoft mb-3">{s.body}</p>
                  {s.rationale && <p className="text-meta text-textMute mb-4 pl-3 border-l-2 border-edge">{s.rationale}</p>}
                  <div className="flex gap-2">
                    <Button size="sm" className="flex-1" onClick={() => respond(s.id, 'accept')} disabled={busy}>Yes, do that</Button>
                    <Button size="sm" variant="secondary" className="flex-1" onClick={() => respond(s.id, 'decline')} disabled={busy}>No thanks</Button>
                  </div>
                </Card>
              ))}
            </div>
          </>
        )}

        <Card className="mb-8">
          <SectionTitle>Your note on the week</SectionTitle>
          <TextArea
            rows={3}
            value={notes ?? data.notes ?? ''}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="How did it actually feel? Anything you want to remember?"
          />
          <Button
            size="sm"
            variant="secondary"
            className="mt-3"
            disabled={busy || notes == null}
            onClick={async () => {
              setBusy(true);
              try {
                await api.post('/review', { week_start: weekStart, notes });
                show('Saved.');
              } catch (err) {
                show(err.message, 'crit');
              } finally {
                setBusy(false);
              }
            }}
          >
            Save note
          </Button>
        </Card>
      </div>
    </div>
  );
}
